/**
 * Bridges a normalized health-store reading (`BodyMeasurementSample`) into the
 * local-api create input for the synced `BodyMeasurement` table. Pure and
 * UI-free so it runs under the app's `test:unit` project.
 *
 * Two rules live here:
 *  - **Deterministic id** — `uuidv5(source|externalId)` so a re-import of the
 *    same reading carries the same primary key and the `ON CONFLICT(id)` upsert
 *    (client and server) is idempotent. No composite unique, no dedupe query.
 *  - **Clamp to now** — an imported `measuredAt` comes from the scale's clock;
 *    a skewed/future-dated reading is clamped to `now` so it can't out-rank a
 *    later real reading when we pick the "current" weight.
 *
 * The imported HealthKit BMI is intentionally dropped — BMI is always derived.
 */
import { RandomUtils } from '@fytness/shared';
import type { BodyMeasurementSample } from '../health-provider/health-provider.types';
import type { CreateBodyMeasurementInput } from '@/lib/local-api/body-measurement';

export interface BodyMeasurementLink {
  /** Denormalized owner id, so the row syncs to its user via PowerSync. */
  userId: string;
}

export class BodyMeasurementMapper {
  static toCreateInput(
    sample: BodyMeasurementSample,
    link: BodyMeasurementLink,
    now: Date = new Date(),
  ): CreateBodyMeasurementInput {
    const measuredAt =
      sample.measuredAt.getTime() > now.getTime() ? now : sample.measuredAt;

    return {
      id: RandomUtils.deterministicUuid(
        `${sample.source}|${sample.externalId}`,
      ),
      source: sample.source,
      externalId: sample.externalId,
      measuredAt: measuredAt.toISOString(),
      weight: sample.weightKg ?? null,
      bodyFatPercent: sample.bodyFatPercent ?? null,
      leanMass: sample.leanMassKg ?? null,
      userId: link.userId,
    };
  }
}
